import { MortgageCalcData, MortgageCalculatorFormValues } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { getItem, setItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types/property';
import { formatNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import brokerSchema from '@propertypal/shared/src/validation/schemas/brokerSchema';
import Button from '@propertypal/web-ui/src/buttons/Button';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import { openToast, ToastTypes } from '@propertypal/web-ui/src/toast/ToastContext';
import { Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import useCachedUser from '../../hooks/useCachedUser';
import { FormWrapper } from './BrokerModal.style';

interface Props {
  show: boolean;
  onClose: () => void;
  mortgageCalculator: MortgageCalcData;
  property: Property;
}

const ERROR_MESSAGE: ToastTypes = {
  type: 'error',
  message: 'Sorry, something went wrong sending your details. Please try again!',
};

const BrokerModal: FunctionComponent<Props> = (props) => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useTheme();
  const { submitLead, product, broker } = props.mortgageCalculator;
  const color = broker?.primaryColour || theme.textDark;
  const secondary = broker?.secondaryColour || theme.primary;

  const initialValues: MortgageCalculatorFormValues = {
    paymentTerm: getDefaultTerm(props.property),
    propertyValue: props.property.price?.price || props.property.price?.minPrice,
    deposit: getDefaultDeposit(props.property),
    interestRate: product?.interestRate || 0,
  };

  const formik = useFormik({
    initialValues: {
      paymentTerm: getItem<string>('defaultTerm') || formatNumber(getDefaultTerm(props.property)),
      propertyValue: formatNumber(initialValues.propertyValue),
      deposit: getItem<string>('defaultDeposit') || formatNumber(getDefaultDeposit(props.property)),
      interestRate: getItem<string>('defaultInterest') || formatNumber(product?.interestRate),
      fromEmail: '',
      fromTelephone: '',
      fromName: '',
    },
    validationSchema: brokerSchema,
    onSubmit: async (values) => {
      if (!sending && !sent) {
        setSending(true);
        if (rememberMe) {
          setItem('formProfile', {
            name: values.fromName,
            email: values.fromEmail,
            phoneNumber: values.fromTelephone,
          });
        }

        trackGaEvent('send_broker_enquiry', { page: 'PDP', location: 'Broker Calculator' });

        const res = await submitLead(values);

        if (res) setSent(true);
        else openToast(ERROR_MESSAGE);
      }
    },
  });

  useCachedUser(props.property.loggedInUser, (profile) => {
    formik.setValues({
      ...formik.values,
      fromEmail: profile.email,
      fromName: profile.name,
      fromTelephone: profile.phoneNumber,
    });
    setRememberMe(true);
  });

  return (
    <WhiteModal maxWidth={600} title="Request Mortgage Advice" show={props.show} onClose={props.onClose}>
      <form onSubmit={formik.handleSubmit}>
        {!sent && (
          <FormWrapper>
            {broker?.defaultLogo && (
              <img
                src={broker.defaultLogo.replace('www-dev.propertypal.net', 'www.propertypal.com')}
                alt={`${broker.name} Broker Calculator`}
              />
            )}

            <TextInput
              label="Your name"
              name="fromName"
              placeholder="Your name"
              onValueChange={formik.handleChange('fromName')}
              value={formik.values.fromName}
              error={formik.touched.fromName && formik.errors.fromName}
              testID="fromName"
            />

            <TextInput
              label="Your phone number"
              name="fromTelephone"
              placeholder="Your phone number"
              onValueChange={formik.handleChange('fromTelephone')}
              value={formik.values.fromTelephone}
              error={formik.touched.fromTelephone && formik.errors.fromTelephone}
              testID="fromTelephone"
            />

            <TextInput
              label="Your email"
              name="fromEmail"
              placeholder="Your email"
              onValueChange={formik.handleChange('fromEmail')}
              value={formik.values.fromEmail}
              error={formik.touched.fromEmail && formik.errors.fromEmail}
              testID="fromEmail"
            />

            <Button
              type="submit"
              loading={sending}
              disabled={sending}
              testID="submitButton"
              color={secondary}
              hoverColor={color}
            >
              Request Mortgage Advice
            </Button>
          </FormWrapper>
        )}

        {sent && (
          <>
            <Text
              data-testid="successText"
              fontSize={14}
              fontWeight="bold"
              align="center"
              style={{ marginTop: '60px' }}
            >
              Thank you for your enquiry.
            </Text>

            <Text fontSize={14} fontWeight="bold" align="center" style={{ marginBottom: '60px' }}>
              A member of our team will be in touch with you shortly.
            </Text>
          </>
        )}

        {broker?.complianceText && (
          <Text fontSize={11.5} mt={50}>
            {broker.complianceText}
          </Text>
        )}
      </form>
    </WhiteModal>
  );
};

export default BrokerModal;
